import {
  ECOMM_ADVANCED_FEATURES,
  type ECommAdvancedFeature,
  type ECommAdvancedFeatureStates,
} from '@choco-admin/types';
import { Button, Chip, Switch } from '@heroui/react';
import React from 'react';
import { FiAlertTriangle } from 'react-icons/fi';

import { AlertCard } from '../../../../../../../components/AlertCard';
import { useNotification } from '../../../../../../../contexts/NotificationContext';
import { ECOMM_SETTINGS_FLAG } from '../../../../../../../featureFlags';
import { type UseUpdateECommAdvancedSettingsResult } from '../../../../../hooks/useUpdateECommAdvancedSettings';
import { LockedFieldTooltip } from '../../../components/IntegrationLockNotice';

interface FeatureCopy {
  label: string;
  description: string;
  /** Something CS should check before turning the feature on. */
  note?: string;
}

const FEATURE_COPY: Record<ECommAdvancedFeature, FeatureCopy> = {
  searchSynonyms: {
    label: 'Customized search (synonyms)',
    description:
      'Distributor admins map the words buyers type to catalog terms, e.g. "cwp" to "causeway prime".',
  },
  productBoosting: {
    label: 'Product boost',
    description:
      'Distributor admins pick brands that rank higher in search and recommendations.',
    note: 'Needs brand data from the integration.',
  },
};

const BLOCK_TITLE = 'eCommerce Advanced features';

/**
 * What a locked control says on hover. Like the Integrated toggle's, it names
 * the real LaunchDarkly key, so whoever is asked can search for it.
 */
export const ECOMM_FEATURES_LOCKED_TOOLTIP = `Read-only for you. To change these features, ask for the ${ECOMM_SETTINGS_FLAG} feature flag to be switched on for you.`;

interface ECommAdvancedFeaturesProps {
  /** Shown in messages. */
  supplierName?: string | null;
  /**
   * What is stored, read with the rest of the distributor. Null when it could
   * not be read.
   */
  features?: ECommAdvancedFeatureStates | null;
  /**
   * The write, owned by `SupplierForm` rather than by this block: the Settings
   * tab unmounts when another tab is opened, and a write lock held here would
   * not survive the round trip (see `useUpdateECommAdvancedSettings`).
   */
  write: UseUpdateECommAdvancedSettingsResult;
  /**
   * Whether this person may switch the features (`portal-can-edit-ecomm-settings`).
   * Without it they still see every feature and its state, read-only.
   */
  canEdit: boolean;
}

interface BlockHeaderProps {
  /** Shown beside the title, once there is a state to count. */
  onCount?: number;
  action?: React.ReactNode;
}

const BlockHeader: React.FC<BlockHeaderProps> = ({ onCount, action }) => (
  <div className="flex items-start justify-between gap-6">
    <div>
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium text-foreground">{BLOCK_TITLE}</p>
        {onCount !== undefined && (
          <Chip
            size="sm"
            variant="flat"
            data-testid="ecomm-advanced-features-count"
          >
            {`${onCount} of ${ECOMM_ADVANCED_FEATURES.length} on`}
          </Chip>
        )}
      </div>
      <p className="mt-1 text-xs text-default-600">
        Turn on what this distributor bought. Changing it takes effect
        immediately, without saving the form.
      </p>
    </div>
    {action}
  </div>
);

const FeatureControls: React.FC<
  ECommAdvancedFeaturesProps & { features: ECommAdvancedFeatureStates }
> = ({ supplierName, features, write: { setFeatures, isSaving }, canEdit }) => {
  const { addNotification } = useNotification();
  // One write at a time: each result replaces the stored settings whole, so a
  // second write racing the first could undo it. `isSaving` comes from
  // `SupplierForm`, so the lock holds even across a trip to another tab.
  const isDisabled = !canEdit || isSaving;

  const name = supplierName || 'This distributor';
  const onCount = ECOMM_ADVANCED_FEATURES.filter(
    (feature) => features[feature]
  ).length;
  const areAllOn = onCount === ECOMM_ADVANCED_FEATURES.length;

  const save = async (
    changes: Partial<ECommAdvancedFeatureStates>,
    {
      title,
      turnOn,
      message,
    }: { title: string; turnOn: boolean; message: string }
  ) => {
    // The disabled controls are the real gate; this keeps a write from going
    // out by any other route once the flag is off.
    if (!canEdit) return;

    const result = await setFeatures(changes);

    if (result.kind === 'error') {
      addNotification({
        title: `${title} not changed`,
        message: result.message,
        type: 'error',
      });
      return;
    }

    addNotification({
      title: `${title} turned ${turnOn ? 'on' : 'off'}`,
      message,
      type: 'success',
    });
  };

  const toggleOne = (feature: ECommAdvancedFeature, turnOn: boolean) =>
    save(
      { [feature]: turnOn },
      {
        title: FEATURE_COPY[feature].label,
        turnOn,
        message: `${name} ${turnOn ? 'now has' : 'no longer has'} this feature.`,
      }
    );

  // Off only once every feature is on. With any feature off, the control turns
  // the rest on, which is the change CS makes when a distributor buys the lot.
  const toggleAll = () => {
    const turnOn = !areAllOn;
    // Typed as the full set, so a new feature cannot be left out of "all".
    const allFeatures: ECommAdvancedFeatureStates = {
      searchSynonyms: turnOn,
      productBoosting: turnOn,
    };
    save(allFeatures, {
      title: BLOCK_TITLE,
      turnOn,
      message: turnOn
        ? `${name} now has all of them.`
        : `${name} no longer has any of them.`,
    });
  };

  return (
    <div className="space-y-3">
      <BlockHeader
        onCount={onCount}
        action={
          <LockedFieldTooltip
            isLocked={!canEdit}
            reason={ECOMM_FEATURES_LOCKED_TOOLTIP}
          >
            <Button
              type="button"
              size="sm"
              color={areAllOn ? 'default' : 'primary'}
              variant={areAllOn ? 'bordered' : 'solid'}
              className="flex-shrink-0"
              onPress={toggleAll}
              isDisabled={isDisabled}
              data-testid="ecomm-advanced-features-toggle-all"
            >
              {areAllOn ? 'Turn all off' : 'Turn all on'}
            </Button>
          </LockedFieldTooltip>
        }
      />

      <div className="divide-y divide-divider rounded-medium border border-default-200">
        {ECOMM_ADVANCED_FEATURES.map((feature) => {
          const { label, description, note } = FEATURE_COPY[feature];

          return (
            <div
              key={feature}
              className="flex items-start justify-between gap-6 p-4"
            >
              <div>
                <p className="text-sm font-medium text-foreground">{label}</p>
                <p className="mt-1 text-xs text-default-600">{description}</p>
                {note && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-warning">
                    <FiAlertTriangle size={12} aria-hidden="true" />
                    {note}
                  </p>
                )}
              </div>
              <LockedFieldTooltip
                isLocked={!canEdit}
                reason={ECOMM_FEATURES_LOCKED_TOOLTIP}
              >
                <Switch
                  isSelected={features[feature]}
                  onValueChange={(turnOn) => toggleOne(feature, turnOn)}
                  isDisabled={isDisabled}
                  size="lg"
                  className="flex-shrink-0"
                  aria-label={label}
                  data-testid={`ecomm-advanced-feature-${feature}`}
                />
              </LockedFieldTooltip>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * The eCommerce Advanced features a distributor has bought, one switch each,
 * and a control that turns them all on or all off.
 *
 * Every change writes straight away, like the access package above it: the
 * settings have their own mutation and are not part of `supplierUpdate`, so
 * they cannot travel with Save.
 *
 * Null settings read as a load failure, not as every feature off. A Choco admin
 * always gets the settings, so null means they could not be read, and showing
 * switches would invite writes that cannot land.
 */
export const ECommAdvancedFeatures: React.FC<ECommAdvancedFeaturesProps> = ({
  features,
  ...props
}) => {
  if (!features) {
    return (
      <div className="space-y-3">
        <BlockHeader />
        <AlertCard
          type="error"
          title="Could not load the eCommerce Advanced features."
          message="Reload the page to try again."
        />
      </div>
    );
  }

  return <FeatureControls {...props} features={features} />;
};
